import { useState, useEffect, useCallback } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Shield, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface ChatbotConsentBannerProps {
  onConsentGranted: () => void;
}

export const ChatbotConsentBanner = ({ onConsentGranted }: ChatbotConsentBannerProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const checkChatbotConsent = useCallback(async () => {
    if (!user) {
      setIsVisible(true);
      return;
    }

    try {
      const userId = user.id || '11111111-2222-3333-4444-555555555555';
      const { data, error } = await supabase
        .from('user_consent')
        .select('chatbot_consent')
        .eq('user_id', userId)
        .eq('consent_version', 'v1.0')
        .maybeSingle();

      if (error) {
        console.error('Error checking chatbot consent:', error);
        setIsVisible(true);
        return;
      }

      if (!data || !data.chatbot_consent) {
        setIsVisible(true);
      } else {
        onConsentGranted();
      }
    } catch (error) {
      console.error('Error checking consent:', error);
      setIsVisible(true);
    }
  }, [user, onConsentGranted]);

  useEffect(() => {
    checkChatbotConsent();
  }, [checkChatbotConsent]);

  const handleGrantConsent = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const userId = user.id || '11111111-2222-3333-4444-555555555555';
      const { error } = await supabase
        .from('user_consent')
        .update({ chatbot_consent: true })
        .eq('user_id', userId)
        .eq('consent_version', 'v1.0');

      if (error) throw error;

      toast({
        title: t('consent.chatbot.granted'),
        description: t('consent.chatbot.grantedDesc'),
      });

      setIsVisible(false);
      onConsentGranted();
    } catch (error) {
      console.error('Error granting consent:', error);
      toast({
        title: t('common.errorTitle'),
        description: t('consent.chatbot.saveFailed'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <Alert className="mb-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20">
      <Shield className="h-4 w-4 text-blue-600" />
      <AlertDescription className="flex items-start justify-between gap-4">
        <div className="flex-1 text-sm text-blue-800 dark:text-blue-200">
          <p className="font-semibold mb-2">{t('consent.chatbot.title')}</p>
          <p className="mb-3">
            {t('consent.chatbot.body')}
          </p>
          <ul className="list-disc pl-5 mb-3 space-y-1">
            <li>{t('consent.chatbot.point1')}</li>
            <li>{t('consent.chatbot.point2')}</li>
            <li>{t('consent.chatbot.point3')}</li>
            <li>{t('consent.chatbot.point4')}</li>
          </ul>
          <div className="flex gap-2">
            <Button
              onClick={handleGrantConsent}
              disabled={isLoading}
              size="sm"
            >
              {isLoading ? t('consent.chatbot.saving') : t('consent.chatbot.acceptContinue')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsVisible(false)}
              size="sm"
            >
              {t('consent.chatbot.decline')}
            </Button>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};
